import { useEffect, useMemo, useRef } from "react";
import { createPortal } from "react-dom";
import { Pause, Play, X } from "lucide-react";
import { usePlayer } from "../context/PlayerContext";
import { Cover } from "./Cover";

const useLines = (song, duration) => useMemo(() => {
  if (song.timing?.length) return { lines: song.timing, synced: true };
  const sung = (song.lyrics || "").split("\n").map((l) => l.trim()).filter((l) => l && !/^\[.*\]$/.test(l));
  const d = duration || 120;
  const step = (d * 0.85) / Math.max(sung.length, 1);
  return { lines: sung.map((text, i) => ({ text, start: d * 0.08 + i * step })), synced: false };
}, [song.timing, song.lyrics, duration]);

const lineTone = (i, idx) => {
  if (i === idx) return "text-white scale-[1.03]";
  if (i < idx) return "text-zinc-600";
  return "text-zinc-400 hover:text-zinc-200";
};

export const KaraokeLyrics = ({ song, big = false, className = "" }) => {
  const p = usePlayer();
  const active = p.current?.id === song.id;
  const { lines, synced } = useLines(song, active ? p.duration : 0);
  const t = active ? p.time : -1;
  let idx = -1;
  lines.forEach((l, i) => { if (t >= l.start - 0.15) idx = i; });
  const box = useRef(null);

  useEffect(() => {
    const b = box.current;
    const el = b?.querySelector(`[data-line="${idx}"]`);
    if (el) b.scrollTo({ top: el.offsetTop - b.clientHeight / 2 + el.clientHeight / 2, behavior: "smooth" });
  }, [idx]);

  if (!lines.length) return <p className="text-zinc-500 text-sm">Instrumentinis kūrinys</p>;
  return (
    <div className={`flex flex-col min-h-0 ${className}`}>
      <span data-testid="karaoke-sync-badge" className={`self-start text-[10px] uppercase tracking-wider font-semibold px-2 py-1 rounded-md mb-3 ${synced ? "bg-emerald-500/15 text-emerald-300" : "bg-zinc-800 text-zinc-400"}`}>
        {synced ? "AI sinchronizuota" : "Apytikslė sinchronizacija"}
      </span>
      <div ref={box} data-testid="karaoke-lines" className={`relative overflow-auto scroll-thin flex-1 ${big ? "py-[36vh]" : "py-[160px]"}`}>
        {lines.map((l, i) => (
          <p key={`${l.start}-${l.text}`} data-line={i} data-testid={`karaoke-line-${i}`} onClick={() => active && p.seek(l.start)}
            className={`cursor-pointer origin-left transition-[color,transform,opacity] duration-300 ${big ? "text-3xl sm:text-4xl lg:text-5xl font-display font-bold py-3 leading-tight" : "text-base py-1.5 font-semibold"} ${lineTone(i, idx)}`}>
            {i === idx && <span className={`inline-block rounded-full bg-[var(--c-orange)] align-middle mr-3 ${big ? "w-3 h-3" : "w-2 h-2"}`} />}{l.text}
          </p>
        ))}
      </div>
    </div>
  );
};

export const KaraokeMode = ({ song, onClose }) => {
  const p = usePlayer();
  const on = p.current?.id === song.id && p.playing;
  return createPortal(
    <div data-testid="karaoke-mode" className="fixed inset-0 z-[70] bg-[var(--c-bg)] flex flex-col rise">
      <Cover song={song} className="absolute inset-0 w-full h-full blur-3xl opacity-25 scale-125" />
      <div className="relative flex items-center gap-4 px-6 sm:px-10 py-6">
        <Cover song={song} className="w-14 h-14 rounded-xl" />
        <div className="min-w-0 flex-1">
          <p className="font-display font-bold truncate">{song.title}</p>
          <p className="text-xs text-zinc-400">{song.author_name}</p>
        </div>
        <button data-testid="karaoke-play" onClick={() => p.play(song, [song])}
          className="w-12 h-12 rounded-full bg-[var(--c-orange)] text-white flex items-center justify-center glow-orange">
          {on ? <Pause className="w-5 h-5" fill="currentColor" /> : <Play className="w-5 h-5 ml-0.5" fill="currentColor" />}
        </button>
        <button data-testid="karaoke-close" onClick={onClose} className="p-3 rounded-full hover:bg-white/10 transition-colors"><X className="w-5 h-5" /></button>
      </div>
      <KaraokeLyrics song={song} big className="relative flex-1 px-6 sm:px-16 lg:px-32 pb-8" />
    </div>,
    document.body
  );
};
